import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { ToastrService } from '@app/services/toastr.service'
import { AuthService } from '@auth/services/auth.service'
import { NbListModule } from '@nebular/theme'
import { Workspace } from '@workspaces/interfaces/workspace.interface'
import { WorkspaceService } from '@workspaces/services/workspace.service'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { DetailsComponent } from './details/details.component'
import { WorkspacesHeaderComponent } from './header/workspaces-header.component'
/**
 * Main workspaces component
 */
@Component({
  selector: 'ngx-workspaces',
  templateUrl: './workspaces.component.html',
  styleUrls: ['./workspaces.component.scss'],
  imports: [CommonModule, NbListModule, DetailsComponent, WorkspacesHeaderComponent]
})
export class WorkspacesComponent implements OnInit, OnDestroy {
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  /** On destroy implementation */
  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }
  /**
   * Workspaces list
   */
  workspaces: Workspace[]

  /**
   * Current worksapce id
   */
  workspaceId: number

  /**
   * Edit form that represents a workspace
   */
  editForm: UntypedFormGroup

  /**
   * Current workspace
   */
  workspace: Workspace

  /**
   * show noMoreWorkspaces text
   */
  noMoreWorkspaces = false

  /**
   * Header component view child
   */
  @ViewChild(WorkspacesHeaderComponent)
  private headerViewChild: WorkspacesHeaderComponent

  /**
   * Creates the component and injects it's dependencies
   * @param workspaceService WorkspaceService
   * @param fb FormBuilder
   * @param toastr ToastrService
   * @param auth AuthService
   * @param router Router
   */
  constructor(private workspaceService: WorkspaceService, private fb: UntypedFormBuilder, private toastr: ToastrService, public auth: AuthService, private router: Router) {}

  /**
   * Updates current workspace list
   * @param workspaces Workspace[]
   */
  updateWorkspaces(workspaces: Workspace[]) {
    this.workspaces = workspaces
  }
  /**
   * Gets the current workspace role
   */
  public get role() {
    return this.auth.currentWorkspace?.role?.name
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    this.editForm = this.fb.group({
      id: ['', Validators.required],
      name: ['', Validators.required],
      slug: ['', Validators.required],
      financialDashboardAccessKey: [1],
      marketingDashboardAccessKey: [1]
    })
  }

  /**
   * Creates an edit form group instance of the workspace
   * @param workspace Workspace
   * @returns void
   */
  edit(workspace: any) {
    if (this.workspaceId === workspace['id']) {
      this.workspaceId = null
      return
    }
    this.workspaceId = workspace['id']
    this.workspace = workspace
    this.editForm.patchValue(workspace)
  }

  /**
   * Saves the current workspace
   * @param workspace any
   */
  save(workspace: any) {
    this.workspaceService
      .updateWorkspace(this.changedFields(this.editForm.value))
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data) => {
          this.clearForm()
          setTimeout(() => {
            document.getElementById(`${this.workspaceId}`).scrollIntoView()
            this.workspaceId = null
          }, 300)

          this.toastr.success('Workspace Updated!', 'Your edits have been saved')
        },
        (error) => {
          if (error.status >= 500) {
            this.toastr.error('Workspace not Updated!', `Error ${error.error}`)
          }
        }
      )
  }

  /**
   * Clears workspace form
   */
  clearForm() {
    this.workspace = null
    this.editForm.patchValue({
      name: '',
      slug: '',
      financialDashboard: 0,
      marketingDashboard: 1
    })
  }

  /**
   * List of changed workspace fields
   */
  changedFields(workspace: any): any {
    const changedEntries = {}
    Object.keys(workspace).forEach((key) => {
      if (this.workspace[key] !== workspace[key]) {
        changedEntries[key] = workspace[key]
      }
    })
    changedEntries['id'] = this.workspace['id']
    return changedEntries
  }
  /**
   * Stable identity for workspace cards so duplicate appends do not remount rows
   * @param _index number
   * @param workspace Workspace
   * @returns number
   */
  trackByWorkspaceId(_index: number, workspace: Workspace): number {
    return workspace?.id
  }

  /**
   * Loads next workspaces
   */
  loadNext() {
    this.headerViewChild?.loadNext()
  }
  /**
   * Emits true when no more workspaces
   */
  showNoMore(noMore: boolean) {
    this.noMoreWorkspaces = noMore
  }
}
